import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useImageActions } from "@features/images/use-images-actions";
import { useLoras } from "@features/loras/use-loras";
import { useEffectPlease } from "@features/utils";
import {
  ArrowsPointingOutIcon,
  CheckIcon,
  HandThumbUpIcon,
  HeartIcon,
  VideoCameraIcon,
} from "@heroicons/react/16/solid";
import {
  ArchiveBoxXMarkIcon,
  ArrowDownCircleIcon,
  EyeSlashIcon,
  HandThumbDownIcon,
} from "@heroicons/react/24/solid";
import { EyeClosedIcon } from "@radix-ui/react-icons";
import { Badge, Spinner, Text, Tooltip } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Link, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { TwinAnimatedImage } from "./twin-animated-image";

const isInCache: Record<string, boolean> = {};
let globalShowExplicit: boolean = false;

export const CameraRollImage = ({
  image: a,
  className,
  style,
  onSelect,
  selected,
  onClickOverride,
  ratio,
  hasSelection,
}: {
  image: ImagesType;
  className?: string;
  style?: React.CSSProperties;
  onSelect?: (select?: boolean) => void;
  selected?: boolean;
  onClickOverride?: (el: HTMLImageElement) => void;
  ratio?: string;
  hasSelection?: boolean;
}) => {
  const { data: showExplicit, refetch } = useQuery(
    "showExplicit",
    () => globalShowExplicit,
  );

  const { t } = useTranslation();
  const { site } = useParams<{ site?: string }>();
  const { loras } = useLoras();

  const [isInView, setIsInView] = useState(!!isInCache[a.id]);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const {
    image: _image,
    refresh,
    restore,
    remove,
    download,
    pendingUpdate: isPendingUpdate,
    setPendingUpdate,
  } = useImage(a.id);
  const image = _image || a;
  const { toggleFavorite } = useImageActions(a.id);

  useEffect(() => {
    // Listen for image updates via websocket
    if (image?.status === "pending") {
      setIsInView(true);
      isInCache[a.id] = true;
    }
  }, [image?.status, a.id, refresh]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            isInCache[a.id] = true;
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }, // Load when 10% of the image is visible
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    const ref = imageRef.current;

    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [image?.status, a.id, imageRef]);

  const aspectRatio = ratio || `${image.size.width / image.size.height}`;

  if (!isInView) {
    return (
      <div
        ref={imageRef}
        className={twMerge(className, "w-full", "transition-all duration-200")}
        style={{
          aspectRatio,
          ...(style || {}),
        }}
      ></div>
    );
  }

  const isFailed = image?.status !== "pending";

  const isLowQuality = !!loras.data?.find(
    (a) =>
      a.id === image.steps[0]?.model_lora &&
      a.technology === "images" &&
      a.subtype === "avatar",
  );

  return (
    <Link
      className={twMerge(
        className,
        "cursor-pointer bg-slate-500 bg-opacity-10 relative overflow-hidden w-full h-full flex items-center justify-center group/image transition-all duration-200",
        isPendingUpdate && "pointer-events-none",
      )}
      style={{
        aspectRatio,
        ...(style || {}),
      }}
      onClick={(e) => {
        if (hasSelection) e.preventDefault();
        if (onClickOverride) {
          e.preventDefault();
          e.stopPropagation();
          onClickOverride(imageRef.current!);
        }
      }}
      to={"/" + site + "/studio/" + image.id}
    >
      <div className="absolute bottom-2 right-2 z-20 space-x-2">
        {image.sub_type === "upscale" && (
          <Badge variant="solid" color="bronze">
            <ArrowsPointingOutIcon className="h-3 w-3" />
            Upscaled
          </Badge>
        )}
        {image.type === "video" && (
          <Badge variant="solid" color="blue">
            <VideoCameraIcon className="h-3 w-3" /> Video
          </Badge>
        )}
      </div>
      {isLowQuality && (
        <div className="absolute top-2 left-2 z-20 space-x-2">
          <Tooltip
            content={t(
              "This image uses the low likeness avatar model, upgrade to high quality for better results.",
            )}
          >
            <Badge variant="solid" color="red">
              LQ
            </Badge>
          </Tooltip>
        </div>
      )}

      {image.status !== "completed" && (
        <>
          <div
            ref={imageRef}
            className={twMerge(
              className,
              "transition-all duration-200 relative overflow-hidden w-full h-full",
              !isFailed && "bg-slate-200 dark:bg-slate-500",
              isFailed && "flex items-center justify-center",
            )}
            style={{
              aspectRatio,
              ...(style || {}),
            }}
          >
            {!isFailed && (
              <PendingImage image={image} className="w-full h-full" />
            )}
            {!!isFailed && (
              <div className="w-full h-full flex flex-col h-24 items-center justify-center">
                <Text size="2">{t("components.camera-roll-image.failed")}</Text>
              </div>
            )}
          </div>
        </>
      )}

      {image.status === "completed" && (
        <>
          <div
            className="absolute w-full h-full opacity-20 z-0"
            style={{
              filter: `blur(20px)`,
              background: `url(${
                image.preview_url || image.url
              }) center center no-repeat`,
              transform: `scale(1.1)`,
              ...style,
            }}
          />

          {image.nsfw && (
            <div
              className={twMerge(
                "z-30 absolute m-auto items-center",
                showExplicit && "top-8 left-8",
              )}
            >
              <ImageButton
                size="1"
                tooltip={t("components.camera-roll-image.show-explicit")}
                onClick={() => {
                  globalShowExplicit = !globalShowExplicit;
                  refetch();
                }}
                alwaysVisible
              >
                {!showExplicit && (
                  <EyeClosedIcon className="w-6 h-6 text-white" />
                )}
                {showExplicit && (
                  <EyeSlashIcon className="w-6 h-6 text-white" />
                )}
              </ImageButton>
            </div>
          )}

          {!onClickOverride && (
            <div className="hidden sm:flex sm:absolute z-30 mx-auto bottom-4 left-2 right-2 flex space-between items-center">
              <ImageButton
                className="sm:flex hidden"
                size="1"
                onClick={() => onSelect?.(!selected)}
                alwaysVisible={selected}
                tooltip={t("components.camera-roll-image.select")}
              >
                <div
                  className={twMerge(
                    "rounded-sm w-4 h-4 border-2 border-slate-500 transition-all duration-200 bg-white bg-opacity-0",
                    selected && "bg-opacity-100 border-white",
                  )}
                >
                  <CheckIcon
                    className={twMerge(
                      "w-4 h-4 text-black m-auto relative -left-0.5 -top-0.5 opacity-0 transition-all duration-200",
                      selected && "opacity-100",
                    )}
                  />
                </div>
              </ImageButton>
              <ImageButton
                size="1"
                onClick={download}
                tooltip={t("components.camera-roll-image.download")}
              >
                <ArrowDownCircleIcon className="w-6 h-6 text-white" />
              </ImageButton>
              <ImageButton
                size="1"
                onClick={() => toggleFavorite(image, setPendingUpdate)}
                tooltip={t("components.camera-roll-image.favorite")}
              >
                <HeartIcon
                  className={`w-6 h-6 ${
                    image.favorite ? "text-red-500" : "text-white"
                  }`}
                />
              </ImageButton>
              <ImageButton
                size="1"
                onClick={async () => {
                  if (image.archived) {
                    restore();
                  } else {
                    remove();
                  }
                }}
                tooltip={
                  !image.archived
                    ? t("components.camera-roll-image.archive")
                    : t("components.camera-roll-image.restore")
                }
              >
                {image.archived && (
                  <HandThumbUpIcon className="w-4 h-4 text-white" />
                )}
                {!image.archived && (
                  <HandThumbDownIcon className="w-4 h-4 text-white" />
                )}
              </ImageButton>
              {!!image.archived && (
                <ImageButton
                  size="1"
                  onClick={async () => {
                    remove();
                  }}
                  tooltip={t("components.camera-roll-image.delete")}
                >
                  <ArchiveBoxXMarkIcon className="w-4 h-4 text-red-500" />
                </ImageButton>
              )}
            </div>
          )}
          {/* Gradient to dark on bottom on group hover */}
          <div
            className="absolute w-full h-full z-20 opacity-0 group-hover/image:opacity-100 transition-all duration-200 pointer-events-none"
            style={{
              background: `linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.5) 100%)`,
            }}
          />

          {/* Click to edit overlay */}
          <div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-all duration-200 pointer-events-none">
            <div className="bg-black bg-opacity-70 px-4 py-2 rounded-lg">
              <Text size="2" weight="medium" className="text-white">
                {t("components.camera-roll-image.click-to-edit")}
              </Text>
            </div>
          </div>

          <TwinAnimatedImage
            divRef={imageRef}
            key={image.id}
            layoutId={"animated-image-" + image.id}
            crossOrigin="anonymous"
            src={image.url || image.preview_url} // Only set src when the image is in view
            onLoad={(e) => {
              // Make image appear
              e.currentTarget.style.opacity = "1";
            }}
            style={{
              aspectRatio,
              ...(style || {}),
            }}
            className={twMerge(
              className,
              "w-full h-full z-10 object-cover flex flex-row flex-wrap bg-slate-500 transition-all duration-200 opacity-0",
              image.nsfw && !showExplicit && "blur-lg",
            )}
          />
        </>
      )}
    </Link>
  );
};

const ImageButton = ({
  onClick,
  size,
  children,
  alwaysVisible,
  tooltip,
  className,
}: {
  onClick: () => void;
  size: "1" | "2";
  children: React.ReactNode;
  alwaysVisible?: boolean;
  tooltip?: string;
  className?: string;
}) => {
  return (
    <Tooltip content={tooltip} side="top" align="center">
      <div
        className={twMerge(
          "opacity-0 group-hover/image:opacity-100 transition-all duration-200 group-hover/image:duration-100",
          alwaysVisible && "opacity-100 sm:opacity-100",
          "bg-black bg-opacity-50 rounded-full m-auto flex items-center justify-center cursor-pointer transition-all duration-200 hover:duration-100 hover:scale-110 hover:bg-opacity-75",
          size === "1" ? "w-10 h-10" : "w-14 h-14",
          className,
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

const getAbstractCanvas = async (
  color1: string,
  color2: string,
  imageUrl?: string,
): Promise<string> => {
  // Create a canvas with the image in background (if defined)
  // Then create a random bi color pattern on top of it
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return imageUrl || "";

  if (imageUrl) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageUrl;
    await new Promise((resolve) => {
      img.onload = () => {
        canvas.width = 64;
        canvas.height = img.height * (64 / img.width);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(true);
      };
    });
  } else {
    canvas.width = 64;
    canvas.height = 64;
  }

  // Create a random bi color pattern
  const patternCanvas = document.createElement("canvas");
  const patternCtx = patternCanvas.getContext("2d");
  if (!patternCtx) return imageUrl || "";

  patternCanvas.width = 16;
  patternCanvas.height = 16;

  // Fill with base color
  patternCtx.fillStyle = color1;
  patternCtx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);

  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"; // or anything else
  patternCtx.font = "bold 16px sans-serif"; // size will affect density
  patternCtx.textBaseline = "top";

  for (let i = 0; i < 24; i++) {
    const x = Math.random() * patternCanvas.width;
    const y = Math.random() * patternCanvas.height;

    const ratio = 0.5 + Math.random() / 2;
    const r = Math.floor(
      parseInt(color1.slice(1, 3), 16) * (1 - ratio) +
        parseInt(color2.slice(1, 3), 16) * ratio,
    );
    const g = Math.floor(
      parseInt(color1.slice(3, 5), 16) * (1 - ratio) +
        parseInt(color2.slice(3, 5), 16) * ratio,
    );
    const b = Math.floor(
      parseInt(color1.slice(5, 7), 16) * (1 - ratio) +
        parseInt(color2.slice(5, 7), 16) * ratio,
    );
    patternCtx.fillStyle = `rgb(${r},${g},${b})`;

    const char = chars[Math.floor(Math.random() * chars.length)];
    patternCtx.fillText(char, x, y);
  }

  // Put pattern on top of the image if it exists
  ctx.globalAlpha = imageUrl ? 0.2 : 1; // Semi-transparent pattern
  ctx.drawImage(patternCanvas, 0, 0, canvas.width, canvas.height);
  ctx.globalAlpha = 1; // Reset alpha for any further drawing
  return canvas.toDataURL("image/png");
};

export const PendingImage = ({
  image,
}: {
  image: ImagesType;
  className?: string;
}) => {
  const promptAnalysis = image?.steps?.find(
    (a) => a.prompt_analysis,
  )?.prompt_analysis;

  const layersContainer = useRef<HTMLDivElement | null>(null);
  const zIndex = useRef(1);
  const colorsRef = useRef<{
    color1: string;
    color2: string;
    latestImage?: string;
  }>({
    color1: promptAnalysis?.color_1 || "black",
    color2: promptAnalysis?.color_2 || "white",
  });
  colorsRef.current.latestImage =
    image?.steps?.filter((a) => a.result_url)?.pop()?.result_url || image?.url;
  colorsRef.current.color1 =
    promptAnalysis?.color_1 || colorsRef.current.color1;
  colorsRef.current.color2 =
    promptAnalysis?.color_2 || colorsRef.current.color2;

  const setAbstractLayer = useCallback(() => {
    if (layersContainer.current) {
      (async () => {
        const newLayer = document.createElement("img");
        newLayer.style.position = "absolute";
        newLayer.style.opacity = "0";
        newLayer.style.transition = "opacity 2s";
        newLayer.style.left = "0";
        newLayer.style.bottom = "0";
        newLayer.style.width = "100%";
        newLayer.style.height = "100%";
        newLayer.style.zIndex = "10";
        newLayer.style.objectFit = "cover";
        newLayer.style.objectPosition = "center";
        newLayer.style.pointerEvents = "none";
        newLayer.style.zIndex = "" + zIndex.current++;
        layersContainer.current?.appendChild(newLayer);

        const dataUrl = await getAbstractCanvas(
          colorsRef?.current?.color1 || "black",
          colorsRef?.current?.color2 === colorsRef?.current?.color1
            ? "white"
            : colorsRef?.current?.color2 || "white",
          colorsRef?.current?.latestImage,
        );
        newLayer.src = dataUrl;

        newLayer.onload = () => {
          newLayer.style.opacity = "1";
          setTimeout(() => {
            try {
              //Remove all child elements except the last one
              if (layersContainer.current) {
                while (layersContainer.current.children.length > 1) {
                  layersContainer.current.removeChild(
                    layersContainer.current.children[0],
                  );
                }
              }
            } catch (e) {
              console.error("Error removing existing layer:", e);
            }
          }, 2500);
        };
      })();
    }
  }, []);

  const interval = useRef<NodeJS.Timeout | null>(null);
  useEffectPlease(() => {
    setAbstractLayer();
    interval.current = setInterval(() => {
      setAbstractLayer();
    }, 3000);
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={layersContainer}
        className={twMerge(
          "absolute w-full h-full left-0 bottom-0 transition-all duration-1000 relative bg-slate-500 scale-110",
        )}
        style={{
          filter: `blur(50px)`,
        }}
      />
      <Spinner className="absolute left-0 right-0 top-0 bottom-0 m-auto shadow-lg text-white h-5 w-5" />
    </>
  );
};
